import React, { useState, useEffect, useRef } from 'react';
import styles from '../styles/HomePage.module.css';
import imageStyles from '../styles/ImageStyles.module.css';
import ProjectSection from './ProjectSection';
import axios from 'axios';
import kaiyaIcon from '../assets/kaiya_icon.png'; // Replace with your futuristic AI assistant icon
import JupiterSwap from '../components/JupiterSwap';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import AiSection from './AiSection';


const BASE_URL = window.location.origin;
const API_URL = 'https://api.baksotiger.com';
console.log("Using API URL:", API_URL);

interface Message {
  agent: string;
  message: string;
}

const HomePage: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const vantaRef = useRef<HTMLDivElement>(null);


  const [sectionsCollapsed, setSectionsCollapsed] = useState({
    project: true,
    family: true,
    aiecosystem: true,
    testimonial: true,
  });
  // Scroll chat to the bottom when new messages appear
  useEffect(() => {
    if (isChatOpen && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, isChatOpen]);

  const handleMouseMove = (e: React.MouseEvent) => {
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  const toggleSection = (sectionName: keyof typeof sectionsCollapsed) => {
    setSectionsCollapsed(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
    if (!isChatOpen && messages.length === 0) {
      setMessages([{ agent: 'BaksoAI', message: 'Hello! I’m BaksoAI, your AI assistant. How can I assist you in learning about Bakso or the $BAKSO ecosystem today?' }]);
    }
  };

  const toggleNav = () => {
    setIsNavOpen(!isNavOpen);
  };

  const convertLinksToHTML = (text: string): string => {
    if (!text) return '';

    // First, handle Markdown-style links: [text](url)
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    let processedText = text.replace(markdownLinkRegex, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

    // Then, make plain URLs clickable too (http/https URLs not already in a tag)
    // Use a more precise regex that won't capture HTML tags
    const urlRegex = /(?<!["\'\(\[<])https?:\/\/[^\s<>]+/g;
    processedText = processedText.replace(urlRegex, (url: string) => {
      // Only replace if it's not already inside an HTML tag
      if (/<a[^>]*>.*?<\/a>/.test(url)) {
        return url;
      }

      // Make sure to trim any trailing periods or commas that might be part of the sentence
      const cleanUrl = url.replace(/[.,;:]$/, '');
      const punctuation = url.slice(cleanUrl.length); // Capture any trimmed punctuation

      return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer">${cleanUrl}</a>${punctuation}`;
    });

    // Replace newlines with <br> tags
    return processedText.replace(/\n/g, '<br />');
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const messageToSend = inputMessage;
    setInputMessage('');
    setMessages(prev => [...prev, { agent: 'You', message: messageToSend }]);
    setIsThinking(true);

    try {
      const fullApiUrl = `${API_URL}/api/kaiya_message`;

      const response = await fetch(fullApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: messageToSend })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      // Don't do HTML processing here - we'll handle it in the render
      setMessages(prev => [...prev, {
        agent: 'BaksoAI',
        message: data.message || 'No response received. How can I assist you?'
      }]);
    } catch (error) {
      console.error('Error sending message to BaksoAI:', error);
      setMessages(prev => [...prev, {
        agent: 'BaksoAI',
        message: 'Sorry, I encountered an error. How else can I assist you?'
      }]);
    } finally {
      setIsThinking(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputMessage.trim()) {
      e.preventDefault();
      handleSendMessage();
    }
  };



  return (
    <>
      <Navbar />
      <div className={styles.homePage}>
        <div className={styles.heroSection}>
          <h1>Meet Bakso!</h1>
          <p>A symbol of hope for Sumatran tiger conservation</p>
         
        </div>

        <div className={styles.featuredSection}>
          <h2>Bakso's Birth and Conservation Impact</h2>
          <div className={styles.tigerDivider}></div>
          <div className={styles.conservationContent}>
            {/* Featured Image from the conservation section */}
            <div className={imageStyles.ligerImage}>
              <img className={styles.conservationImage} src="/assets/sleepy.png" alt="Sleepy Bakso" />
              <div className={imageStyles.imageCaption}>
                <p className={styles.photoCredit}>
                  Photo credit: <a href="https://www.instagram.com/drmarkatdisney/reel/DCPruLpveNq/" target="_blank" rel="noopener noreferrer">Dr. Mark Penning</a>
                </p>
              </div>
            </div>

            <div>
              <p>
                Bakso is a rare baby Sumatran tiger born in September 2024 at Disney's Animal Kingdom. On January 29, 2025,
                he made his much-anticipated debut at the Maharaja Jungle Trek, instantly capturing the spotlight and
                creating a media frenzy that drew the attention of millions. With fewer than 400 Sumatran tigers remaining
                in the wild, his birth marked a major milestone for conservation efforts.
              </p>
              <p>
                Since his debut, thousands of fans have eagerly lined up to catch a glimpse of this majestic creature,
                while social media has been flooded with adorable snapshots of this fluffy bundle of joy.
                With his playful spirit, bold stripes, and curious energy, Bakso has become a fan favorite both in the
                park and online—amassing millions of views across platforms including his own TikTok.
              </p>
              <p>
                Thanks to the incredible caretakers at Disney's Animal Kingdom, Bakso arrived safely into the world
                and continues to thrive. His story is just beginning and serves as a beacon of hope for tiger conservation,
                inspiring efforts to preserve this critically endangered species.
              </p>
              <p>
                Curious to learn more? Or maybe you'd love to have an AMA with Bakso? Join our <a href="https://www.t.me/baksosolportal" target="_blank" rel="noopener noreferrer">Telegram Community</a>,
                where our outstanding <strong>BaksoAI</strong> and admins are ready to answer all your questions! 🐾
              </p>
            </div>
          </div>
        </div>



        {/* Insert the Project Section component */}
        {/* Collapsible Project Section */}
        <div className={styles.collapsibleSection}>
          <button
            className={styles.sectionHeader}
            onClick={() => toggleSection('project')}
            aria-expanded={!sectionsCollapsed.project}
          >
            <h2>$BAKSO Token</h2>
            <span className={`${styles.chevron} ${sectionsCollapsed.project ? styles.chevronCollapsed : styles.chevronExpanded}`}>
              ▼
            </span>
          </button>

          <div className={`${styles.sectionContent} ${sectionsCollapsed.project ? styles.contentCollapsed : styles.contentExpanded}`}>
            <ProjectSection />
          </div>
        </div>

        {/* Insert the Project Section component */}
        {/* Collapsible Project Section 
        <div className={styles.collapsibleSection}>
          <button
            className={styles.sectionHeader}
            onClick={() => toggleSection('aiecosystem')}
            aria-expanded={!sectionsCollapsed.project}
          >
            <h2>BaksoAI Ecosystem</h2>
            <span className={`${styles.chevron} ${sectionsCollapsed.aiecosystem ? styles.chevronCollapsed : styles.chevronExpanded}`}>
              ▼
            </span>
          </button>

          <div className={`${styles.sectionContent} ${sectionsCollapsed.aiecosystem ? styles.contentCollapsed : styles.contentExpanded}`}>

            <AiSection />
          </div>
        </div>*/}

        {/* Collapsible Family Section */}
        <div className={styles.collapsibleSection}>
          <button
            className={styles.sectionHeader}
            onClick={() => toggleSection('family')}
            aria-expanded={!sectionsCollapsed.family}
          >
            <h2>Meet Bakso's Family</h2>
            <span className={`${styles.chevron} ${sectionsCollapsed.family ? styles.chevronCollapsed : styles.chevronExpanded}`}>
              ▼
            </span>
          </button>

          <div className={`${styles.sectionContent} ${sectionsCollapsed.family ? styles.contentCollapsed : styles.contentExpanded}`}>

            <div className={styles.featuredItems}>
              <div className={styles.featuredItem}>
                <div className={`${imageStyles.tigerImage}`}>
                  <img src="/images/baksopfp.jpg" alt="Bakso" />
                  <div className={imageStyles.imageCaption}>
                    <h4>Bakso</h4>
                    <p>Bakso!</p>
                    <p className={styles.photoCredit}>
                      Photo credit: <a href="https://www.instagram.com/p/DGfya6fRL7W/" target="_blank" rel="noopener noreferrer">TheDakGuy</a>
                    </p>
                  </div>
                </div>

                <div className={styles.itemContent}>
                  <h3>Bakso</h3>
                  <p>
                    Born in September of 2024, Bakso is a playful and curious Sumatran tiger cub who resides at the Maharaja Jungle Trek in Disney's Animal Kingdom. Bakso is apart of Disney's continued conservation efforts to protect the critically endangered Sumatran tiger species.
                  </p>
                </div>
              </div>

              <div className={styles.featuredItem}>
                {/* Tiger-striped border image - Sohni */}
                <div className={`${imageStyles.tigerImage}`}>
                  <img src="/images/sohni.jpg" alt="Sohni - Bakso's mother" />
                  <div className={imageStyles.imageCaption}>
                    <h4>Sohni</h4>
                    <p>Bakso's loving mother</p>
                    <p className={styles.photoCredit}>
                      Photo credit: <a href="https://www.instagram.com/p/DFGXVB_xB-E/" target="_blank" rel="noopener noreferrer">TheDakGuy</a>
                    </p>
                  </div>
                </div>
                <div className={styles.itemContent}>
                  <h3>Sohni - Proud Mother</h3>
                  <p>
                    Sohni, a beautiful Sumatran tiger at Disney Animal Kingdom, is part of Disney's
                    conservation efforts to preserve her critically endangered species. The animal care
                    team is overjoyed with this precious new addition, and Bakso's arrival is making waves
                    not only among the staff but around the world.
                  </p>
                </div>
              </div>

              <div className={styles.featuredItem}>
                {/* Standard image - Conrad */}
                <div className={imageStyles.tigerImage}>
                  <img src="/images/conrad.jpg" alt="Conrad - Bakso's father" />
                  <div className={imageStyles.imageCaption}>
                    <h4>Conrad</h4>
                    <p>Bakso's powerful father</p>
                    <p className={styles.photoCredit}>
                      Photo credit: <a href="https://www.instagram.com/p/DGlC5YQshPU/" target="_blank" rel="noopener noreferrer">TheDakGuy</a>
                    </p>
                  </div>
                </div>

                <div className={styles.itemContent}>
                  <h3>Conrad - Majestic Father</h3>
                  <p>
                    Conrad, a magnificent Sumatran tiger residing at Disney's Animal Kingdom, is more than
                    just a striking presence—he is a proud father and a vital ambassador for his species.
                    Conrad plays a key role in Disney's commitment to conserving the critically endangered
                    Sumatran tiger species.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.testimonialSection}>
          <h2>Conservation Efforts</h2>
          <p className={styles.testimonialQuote}>
            "As one of the few Sumatran tigers in the U.S., Bakso and his family help raise awareness about
            the species' plight and contribute to global conservation efforts through the Species Survival Plan,
            an initiative focused on protecting endangered species through breeding and public education."
          </p>
          <p className={styles.testimonialAuthor}>— Disney's Animal Kingdom Conservation Team</p>
        </div>

        <div className={styles.testimonialSection}>
          <h2>Disclaimer</h2>
          <p className={styles.testimonialQuote}>
           Disclaimer: The $BAKSO token is a community-driven fan and meme token created for entertainment purposes only. It is not an investment vehicle and should not be considered financial advice.
          </p>
        </div>



        {/* Chat Button */}
        <button className={styles.helpButton} onClick={toggleChat}>Ask BaksoAI</button>

        {/* Chat Window */}
        {isChatOpen && (
          <div className={styles.chatWindow}>
            <div className={styles.chatHeader}>
              <img src={kaiyaIcon} alt="BaksoAI" className={styles.kaiyaIcon} />
              <h3>BaksoAI - Your AI Helper</h3>
              <button className={styles.closeChat} onClick={toggleChat}>×</button>
            </div>
            <div className={styles.chatMessages} ref={chatContainerRef}>
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`${styles.chatMessage} ${msg.agent === 'You' ? styles.userMessage : styles.kaiyaMessage}`}
                >
                  <strong>{msg.agent}:</strong> <span dangerouslySetInnerHTML={{ __html: convertLinksToHTML(msg.message) }} />
                </div>
              ))}
              {isThinking && (
                <div className={styles.chatMessage}>
                  <strong>BaksoAI:</strong> Thinking<span className={styles.typingIndicator}></span>
                </div>
              )}
            </div>
            <div className={styles.chatInput}>
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask BaksoAI anything..."
                className={styles.messageInput}
              />
              <button onClick={handleSendMessage} className={styles.sendButton}>Send</button>
            </div>

          </div>
        )}

      </div>
      <Footer />
    </>

  );
};

export default HomePage;